import { AlertTriangle, CircleAlert } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { type BuildingStorageQuota } from "../services/buildingStorageQuota";
import {
  availableStorageLabel,
  storageCopy,
  storageState,
  uploadsBlocked,
} from "../utils/storagePresentation";
import { StorageActions } from "./StorageActions";
import { cn } from "@/lib/utils";
import { billing as billingEn } from "@/lib/i18n/translations/en/billing";
import { billing as billingEt } from "@/lib/i18n/translations/et/billing";

const billingCopy = { en: billingEn, et: billingEt };

function ReservedStorageNote({ quota, message }: { quota: BuildingStorageQuota; message: string }) {
  if (!uploadsBlocked(quota)) return null;
  if (quota.reservedBytes <= 0 || storageState(quota) === "full") return null;
  return <p>{message}</p>;
}

export function StorageUploadNotice({
  quota,
  buildingId,
}: {
  quota: BuildingStorageQuota;
  buildingId: string;
}) {
  const { language } = useLanguage();
  const words = storageCopy[language];
  if (quota.activationPending)
    return (
      <p role="status" className="text-sm text-muted-foreground">
        {billingCopy[language].plans.activationPending}
      </p>
    );
  const blocked = uploadsBlocked(quota);
  if (!blocked && storageState(quota) !== "near") return null;
  const Icon = blocked ? CircleAlert : AlertTriangle;
  const template = blocked ? words.fullUpload : words.nearUpload;
  return (
    <div
      role={blocked ? "alert" : "status"}
      data-testid="storage-upload-notice"
      className={cn(
        "space-y-2 rounded-lg border p-3 text-sm",
        blocked
          ? "border-destructive/30 bg-destructive/5 text-destructive"
          : "border-amber-600/30 bg-amber-500/10 text-amber-800 dark:text-amber-300"
      )}
    >
      <p className="flex items-start gap-2">
        <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <span>{template.replace("{remaining}", availableStorageLabel(quota))}</span>
      </p>
      <ReservedStorageNote quota={quota} message={words.reservedUpload} />
      {blocked && <StorageActions buildingId={buildingId} actionId="get_more_space_upload" />}
    </div>
  );
}
